import { Phone, Mail, MapPin, MessageCircle, Clock } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryForm";
import { BRAND } from "../data/content";
import { usePageMeta } from "../hooks/use-page-meta";
import { track } from "../lib/tracking";

const CARDS = [
  {
    icon: Phone,
    title: "Call",
    lines: [BRAND.phonePrimary, `Alternate: ${BRAND.phoneSecondary}`],
    href: BRAND.phonePrimaryHref,
    testid: "contact-call-card",
    event: "call_click",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp",
    lines: ["Fastest response, any day"],
    href: BRAND.whatsapp,
    testid: "contact-whatsapp-card",
    event: "whatsapp_click",
  },
  {
    icon: Mail,
    title: "Email",
    lines: [BRAND.email],
    href: `mailto:${BRAND.email}`,
    testid: "contact-email-card",
  },
  {
    icon: MapPin,
    title: "Visit",
    lines: [BRAND.address],
    href: BRAND.directions,
    testid: "contact-directions-card",
    event: "directions_click",
  },
];

export default function Contact() {
  usePageMeta(
    "Contact | Celebrity Bouncers — Request Event Security",
    "Request a quotation for premium event security and VIP support. Call, WhatsApp or send an enquiry — serving Andhra Pradesh and Telangana."
  );
  return (
    <>
      <PageHero
        testid="contact-page-hero"
        eyebrow="Contact"
        title="Tell Us About Your Event"
        description="A short, private conversation is all it takes to begin. Share your details below, or reach the coordination team directly."
      />
      <section className="container-x py-16 sm:py-24" data-testid="contact-cards">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.title} delay={i * 0.07}>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={c.testid}
                  onClick={c.event ? () => track(c.event, { placement: "contact_page" }) : undefined}
                  className="group flex h-full flex-col border border-white/[0.07] bg-navy/50 p-7 transition-all duration-500 hover:border-gold/40"
                >
                  <Icon size={20} className="text-gold" />
                  <h2 className="mt-4 font-serif text-xl font-medium text-ivory">{c.title}</h2>
                  {c.lines.map((l) => (
                    <p key={l} className="mt-2 text-sm leading-relaxed text-steel">{l}</p>
                  ))}
                </a>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="mt-8">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-steel/70">
            <Clock size={13} className="text-gold" /> 24/7 enquiry assistance — calls and WhatsApp
          </p>
        </Reveal>
      </section>
      <section className="border-t border-gold/10 bg-navy/40 py-16 sm:py-24" data-testid="contact-form-section">
        <div className="container-x max-w-4xl">
          <Reveal>
            <p className="eyebrow">Private Enquiry</p>
            <h2 className="mt-4 font-serif text-3xl font-medium text-ivory sm:text-4xl">
              Request a Customised Quotation
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-steel sm:text-base">
              Your event details stay private and are used only to respond to this enquiry.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <EnquiryForm testidPrefix="contact-enquiry" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
